import { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { checkActionCode, applyActionCode } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { logout } from '@/services/auth/authService';
import { analyticsService } from '@/services/analytics/analyticsService';
import { useLocalization } from '@/hooks/useLocalization';
import { showToast } from '@/utils/globalToast';
import { ROUTES } from '@/app/router/routes';
import { AppHeader } from '@/components/layout/AppHeader';
import { AppFooter } from '@/components/layout/AppFooter';
import { ProgressHud, authControlStyles as controls } from './components/AuthControls';
import styles from './VerifyEmailPage.module.css';

/**
 * VerifyEmailPage — handles email verification link from Firebase.
 *
 * Mirrors Flutter verify_email_signup.dart + WebSignupBloc.onClickVerifyEmailSignup.
 *
 * This page is reached when a user clicks the email verification link sent by Firebase.
 * The URL contains query parameters: mode, oobCode, tenantId, emailId.
 *
 * Flow:
 *   1. Extract query params from URL
 *   2. Show confirmation page with email and "Verify" button
 *   3. On click: checkActionCode → applyActionCode
 *   4. On success: navigate to /emailVerified
 *   5. On failure (expired or missing code): toast WebVerifyEmail.emailSignUpCodeExpired
 *      for 10 s → /login
 *
 * Flutter source files:
 *   - presentation/web/auth/verify_email_signup/verify_email_signup.dart
 *   - application/web_auth/web_signup/web_signup_bloc.dart (_OnClickVerifyEmailSignup)
 *   - infrastructure/auth/i_auth_repository.dart (verifyEmailSignup)
 */

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLocalization();

  const [isLoading, setIsLoading] = useState(false);

  const oobCode = searchParams.get('oobCode') ?? '';
  const tenantId = searchParams.get('tenantId') ?? '';
  const emailId = searchParams.get('emailId') ?? '';

  // Set tenant ID from URL params if present
  useEffect(() => {
    if (tenantId && auth.tenantId !== tenantId) {
      auth.tenantId = tenantId;
    }
  }, [tenantId]);

  const goToEmailVerified = useCallback(() => {
    void analyticsService.logScreenView({ screenName: ROUTES.EMAIL_VERIFIED });
    navigate(ROUTES.EMAIL_VERIFIED, { replace: true, state: { emailId } });
  }, [navigate, emailId]);

  const handleVerify = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);

    try {
      // Step 1: Check action code validity (Firebase rejects expired or missing codes)
      await checkActionCode(auth, oobCode);

      // Step 2: Check if current user matches the verification email
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.email === emailId) {
        // Same user — apply action code, then reload so emailVerified is current
        // (Flutter polls reload() until it flips)
        await applyActionCode(auth, oobCode);
        await currentUser.reload();
        // New token carries email_verified=true for the backend and updates the store
        await currentUser.getIdToken(true);
        goToEmailVerified();
      } else {
        // Different user or not logged in
        // Revoke tokens, clear MFA flag, sign out, then apply action code
        // Mirrors Flutter's isSameUser=false path
        try {
          await logout();
        } catch {
          // continue even if logout fails
        }

        // Wait briefly (mirrors Flutter's Future.delayed(1000ms))
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Set tenant ID again after logout
        if (tenantId) {
          auth.tenantId = tenantId;
        }

        await applyActionCode(auth, oobCode);
        goToEmailVerified();
      }
    } catch {
      // Action code expired or invalid
      setIsLoading(false);
      showToast('Your request to verify email link has been expired', 'error', 10000);
      void analyticsService.logScreenView({ screenName: ROUTES.LOGIN });
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }, [oobCode, emailId, tenantId, isLoading, navigate, goToEmailVerified]);

  return (
    <div className={styles.page}>
      <div className={styles.screen}>
        <AppHeader />

        <div className={styles.content}>
          <div className={styles.card}>
            <h1 className={styles.title}>{t('Please confirm your email verification')}</h1>
            <p className={styles.email}>{`${t('Email ID')}: ${emailId}`}</p>

            {/* CustomButton(backgroundColor: primaryColorDark) */}
            <button
              type="button"
              onClick={handleVerify}
              disabled={isLoading}
              className={`${controls.customButton} ${controls.customButtonAccent} ${styles.verifyBtn}`}
            >
              {t('Verify')}
            </button>
          </div>
        </div>
      </div>

      {/* The footer is hidden while loading, as in Flutter */}
      {!isLoading && <AppFooter />}

      {/* Flutter ModalProgressHUD */}
      {isLoading && <ProgressHud />}
    </div>
  );
}
